import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Select,
  MenuItem,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Chip,
  Snackbar,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import { useNavigate } from 'react-router-dom';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SyncIcon from '@mui/icons-material/Sync';
import { tokens } from '../../theme';
import {
  uploadFile,
  getMappingPreferences,
  saveMappingPreferences,
  validateMapping,
  getPreview,
  executeImport,
  getImportStatus,
  cancelImport,
  ImportStatus,
} from '../../services/importService';

const ContactImport = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [fieldMapping, setFieldMapping] = useState({});
  const [previewContacts, setPreviewContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [importDialog, setImportDialog] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const [importId, setImportId] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Polling ref for cleanup
  const pollingRef = useRef(null);
  
  const steps = ['Télécharger fichier', 'Mapper les champs', 'Aperçu & Confirmation'];
  
  // Available fields for mapping - organized by category
  const availableFields = [
    { value: 'skip', label: '-- Ignorer --' },
    
    // Only email is required, firstName and lastName are optional
    { value: 'firstName', label: 'Prénom' },
    { value: 'lastName', label: 'Nom' },
    { value: 'email', label: 'Email *', required: true },
    
    // Contact information
    { value: 'phone', label: 'Téléphone' },
    
    // Address fields
    { value: 'address', label: 'Adresse' },
    { value: 'city', label: 'Ville' },
    { value: 'postalCode', label: 'Code postal' },
    { value: 'country', label: 'Code pays' },
    { value: 'countryName', label: 'Nom du pays' },
    { value: 'state', label: 'État/Province' },
    
    // Professional information
    { value: 'company', label: 'Entreprise' },
    { value: 'jobTitle', label: 'Titre du poste' },
    { value: 'profession', label: 'Profession' },
    
    // Personal information
    { value: 'nationality', label: 'Nationalité' },
    { value: 'birthDate', label: 'Date de naissance (AAAA/MM/JJ)' },
    { value: 'gender', label: 'Genre' },
    { value: 'maritalStatus', label: 'État civil' },
    { value: 'kids', label: 'Enfants' },
    
    // Preferences
    { value: 'language', label: 'Langue' },
    { value: 'levelOfEducation', label: 'Niveau d\'éducation' },
    { value: 'levelOfSatisfaction', label: 'Niveau de satisfaction' },
    { value: 'formOfCommunication', label: 'Forme de communication' },
    { value: 'preferredChannel', label: 'Canal préféré' },
    
    // Customer information
    { value: 'subscriptionStatus', label: 'Statut de l\'abonnement' },
    { value: 'customerType', label: 'Type de client' },
    
    // Social media
    { value: 'facebookId', label: 'ID Facebook' },
    
    // Other
    { value: 'interests', label: 'Intérêts' },
  ];

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  // Load saved mapping preferences on component mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const prefs = await getMappingPreferences();
        if (prefs.mapping && Object.keys(prefs.mapping).length > 0) {
          setFieldMapping(prefs.mapping);
        }
      } catch (err) {
        console.error('Error loading preferences:', err);
      }
    };
    loadPreferences();
  }, []);

  // Start polling for import status
  const startPolling = (id) => {
    // Clear any existing polling
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    
    // Poll every 1.5 seconds
    pollingRef.current = setInterval(async () => {
      try {
        const status = await getImportStatus(id);
        setImportStatus(status);
        
        // Stop polling when import is complete
        if (
          status.status === ImportStatus.COMPLETED ||
          status.status === ImportStatus.FAILED ||
          status.status === ImportStatus.CANCELLED
        ) {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
          setLoading(false);
        }
      } catch (err) {
        console.error('Error polling import status:', err);
      }
    }, 1500);
  };

  const handleNext = async () => {
    if (activeStep === 1) {
      // Validate mapping before proceeding to preview
      setLoading(true);
      try {
        const validation = await validateMapping(fieldMapping);
        if (!validation.valid) {
          setValidationErrors(validation.errors);
          setLoading(false);
          return;
        }
        
        // Get preview
        const previewData = await getPreview(fileId, fieldMapping);
        setPreviewContacts(previewData.contacts);
        setValidationErrors([]);
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      } catch (err) {
        const errorDetail = err.response?.data?.detail || 'Erreur lors de la validation';
        
        // If file not found, reset to step 0 to allow re-upload
        if (err.response?.status === 404) {
          setError(errorDetail + ' Veuillez télécharger à nouveau votre fichier.');
          setActiveStep(0);
          setFileId(null);
          setSelectedFile(null);
        } else {
          setError(errorDetail);
        }
      } finally {
        setLoading(false);
      }
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setError(null);
    setValidationErrors([]);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setLoading(true);
      setError(null);
      
      try {
        const uploadData = await uploadFile(file);
        setFileId(uploadData.file_id);
        setCsvHeaders(uploadData.headers);
        setTotalRows(uploadData.total_rows);
        
        // Initialize mapping with existing preferences or empty
        const initialMapping = {};
        uploadData.headers.forEach(header => {
          initialMapping[header] = fieldMapping[header] || 'skip';
        });
        setFieldMapping(initialMapping);
        
        // Auto-advance to step 2
        setActiveStep(1);
      } catch (err) {
        setError(err.response?.data?.detail || 'Erreur lors du téléchargement du fichier');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleMappingChange = async (csvColumn, profileField) => {
    const newMapping = {
      ...fieldMapping,
      [csvColumn]: profileField,
    };
    setFieldMapping(newMapping);
    
    // Save preferences
    try {
      await saveMappingPreferences(newMapping);
    } catch (err) {
      console.error('Error saving preferences:', err);
    }
  };

  const handleConfirm = async () => {
    setImportDialog(true);
    setLoading(true);
    setError(null);
    setImportStatus(null);
    
    try {
      // Start background import
      const result = await executeImport(fileId, fieldMapping);
      setImportId(result.import_id);
      
      // Start polling for status updates
      startPolling(result.import_id);
      
      // Set initial status
      setImportStatus({
        status: ImportStatus.PENDING,
        total_rows: totalRows,
        processed_count: 0,
        success_count: 0,
        error_count: 0,
        skipped_count: 0,
        progress_percentage: 0,
        status_message: 'Démarrage de l\'importation...',
      });
    } catch (err) {
      const errorDetail = err.response?.data?.detail || 'Erreur lors de l\'importation';
      
      // If file not found, close dialog and reset to step 0
      if (err.response?.status === 404) {
        setImportDialog(false);
        setError(errorDetail + ' Veuillez télécharger à nouveau votre fichier.');
        setActiveStep(0);
        setFileId(null);
        setSelectedFile(null);
      } else {
        setError(errorDetail);
      }
      setLoading(false);
    }
  };

  const handleCancelImport = async () => {
    if (importId) {
      try {
        await cancelImport(importId);
        // Refresh status
        const status = await getImportStatus(importId);
        setImportStatus(status);
      } catch (err) {
        console.error('Error cancelling import:', err);
      }
    }
  };

  const handleCloseImportDialog = () => {
    // Stop any ongoing polling
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    
    setImportDialog(false);
    if (importStatus && importStatus.success_count > 0) {
      navigate('/contacts');
    }
  };

  // Close dialog but continue import in background
  const handleContinueInBackground = () => {
    // Stop polling in this component (the history dialog will poll)
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    
    setImportDialog(false);
    setSnackbarMessage('L\'importation continue en arrière-plan. Consultez l\'historique des imports pour suivre la progression.');
    setSnackbarOpen(true);
    
    // Reset the form for a new import
    setActiveStep(0);
    setSelectedFile(null);
    setFileId(null);
    setCsvHeaders([]);
    setTotalRows(0);
    setFieldMapping({});
    setPreviewContacts([]);
    setImportStatus(null);
    setImportId(null);
    setLoading(false);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleGoToContacts = () => {
    setSnackbarOpen(false);
    navigate('/contacts');
  };

  const isImportInProgress = importStatus && (
    importStatus.status === ImportStatus.PENDING || 
    importStatus.status === ImportStatus.PROCESSING
  );

  const isImportComplete = importStatus && (
    importStatus.status === ImportStatus.COMPLETED ||
    importStatus.status === ImportStatus.FAILED ||
    importStatus.status === ImportStatus.CANCELLED
  );

  return (
    <Box sx={{ p: 3, backgroundColor: colors.grey[100], minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight={600} color={colors.primary[500]}>
          Importer des contacts
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/contacts')}
          sx={{
            borderColor: colors.grey[400],
            color: colors.grey[700],
            '&:hover': {
              borderColor: colors.grey[600],
              backgroundColor: colors.grey[200],
            }
          }}
        >
          Retour
        </Button>
      </Box>

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                StepIconProps={{
                  sx: {
                    '&.Mui-active': {
                      color: colors.primary[500],
                    },
                    '&.Mui-completed': {
                      color: colors.primary[500],
                    },
                  }
                }}
              >
                <Typography variant="body2" fontWeight={activeStep === index ? 600 : 400}>
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Step Content */}
      <Paper sx={{ p: 4, borderRadius: 2, minHeight: 400 }}>
        {/* Step 1: Upload File */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="h5" fontWeight={600} mb={2}>
              Étape 1 : Importer votre fichier
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={4}>
              Téléchargez un fichier CSV contenant vos contacts. Les formats supportés incluent :{' '}
              <Box component="span" fontWeight={500}>
                Nom, Email, Téléphone, Entreprise...
              </Box>
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" mt={2}>
                  Traitement du fichier...
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  border: `2px dashed ${colors.grey[400]}`,
                  borderRadius: 2,
                  p: 6,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  backgroundColor: selectedFile ? alpha(colors.primary[100], 0.3) : 'transparent',
                  '&:hover': {
                    backgroundColor: colors.grey[100],
                    borderColor: colors.primary[500],
                  }
                }}
                onClick={() => document.getElementById('file-upload').click()}
              >
                <input
                  type="file"
                  id="file-upload"
                  accept=".csv"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
                <CloudUploadIcon 
                  sx={{ 
                    fontSize: 48, 
                    color: selectedFile ? colors.primary[500] : colors.grey[500],
                    mb: 2 
                  }} 
                />
                <Typography 
                  variant="body1" 
                  fontWeight={600} 
                  color={selectedFile ? colors.primary[500] : colors.primary[600]}
                  sx={{ cursor: 'pointer' }}
                >
                  {selectedFile ? `📄 ${selectedFile.name}` : '📂 Cliquez ici pour sélectionner un fichier CSV'}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  ou glissez-déposez votre fichier
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Step 2: Field Mapping */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="h5" fontWeight={600} mb={2}>
              Étape 2 : Mapper les champs
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Associez les colonnes de votre fichier CSV aux champs de Qilinsa Amplify.
            </Typography>
            <Typography variant="body2" color="error" mb={2}>
              * Champ obligatoire
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              Pour chaque colonne de votre fichier CSV, sélectionnez le champ de destination correspondant. 
              Seul le champ Email (*) est obligatoire. 
              Tous les autres champs (y compris Prénom et Nom) sont optionnels.
            </Alert>

            {validationErrors.length > 0 && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {validationErrors.map((err, idx) => (
                  <div key={idx}>{err}</div>
                ))}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 400, overflowY: 'auto', pr: 2 }}>
              {csvHeaders.map((header, index) => (
                <Box key={`mapping-${index}-${header}`} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1" color="text.primary" sx={{ minWidth: 200 }}>
                    Colonne "{header}"
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      Champ de destination
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 250 }} variant="outlined">
                      <Select
                        id={`mapping-select-${index}`}
                        value={fieldMapping[header] || 'skip'}
                        onChange={(e) => handleMappingChange(header, e.target.value)}
                        displayEmpty
                      >
                        {availableFields.map((field) => (
                          <MenuItem 
                            key={`${index}-${field.value}`} 
                            value={field.value}
                            sx={{
                              fontWeight: field.required ? 600 : 400,
                              color: field.required ? colors.primary[500] : 'inherit'
                            }}
                          >
                            {field.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{
                  borderColor: colors.grey[400],
                  color: colors.grey[700],
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: colors.grey[600],
                    backgroundColor: colors.grey[200],
                  }
                }}
              >
                Précédent
              </Button>
              <Button
                variant="contained"
                endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
                onClick={handleNext}
                disabled={loading}
                sx={{
                  backgroundColor: colors.primary[500],
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: colors.primary[600],
                  }
                }}
              >
                {loading ? 'Validation...' : 'Suivant'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 3: Preview & Confirmation */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="h5" fontWeight={600} mb={2}>
              Étape 3 : Aperçu & Confirmation
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Voici un aperçu des premiers contacts importés. Vérifiez avant de confirmer.
            </Typography>
            <Typography variant="body2" fontWeight={600} color={colors.primary[500]} mb={4}>
              Total de contacts à importer : {totalRows}
            </Typography>

            <TableContainer sx={{ mb: 4, maxHeight: 400 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: colors.grey[100], color: colors.grey[700] }}>
                      Prénom
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: colors.grey[100], color: colors.grey[700] }}>
                      Nom
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: colors.grey[100], color: colors.grey[700] }}>
                      Email
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: colors.grey[100], color: colors.grey[700] }}>
                      Téléphone
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewContacts.map((contact, index) => (
                    <TableRow key={index}>
                      <TableCell>{contact.firstName || '-'}</TableCell>
                      <TableCell>{contact.lastName || '-'}</TableCell>
                      <TableCell>{contact.email || '-'}</TableCell>
                      <TableCell>{contact.phone || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Alert severity="info" sx={{ mb: 4 }}>
              <strong>Import en arrière-plan :</strong> L'importation se fait en arrière-plan. 
              Vous pouvez suivre la progression en temps réel ou fermer cette fenêtre et vérifier l'historique des imports.
            </Alert>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{
                  borderColor: colors.grey[400],
                  color: colors.grey[700],
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: colors.grey[600],
                    backgroundColor: colors.grey[200],
                  }
                }}
              >
                Précédent
              </Button>
              <Button
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={handleConfirm}
                sx={{
                  backgroundColor: '#4caf50',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#45a049',
                  }
                }}
              >
                Confirmer l'import
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Import Progress Dialog */}
      <Dialog
        open={importDialog}
        onClose={isImportInProgress ? handleContinueInBackground : handleCloseImportDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isImportInProgress ? (
            <>
              <SyncIcon sx={{ animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
              Importation en cours...
            </>
          ) : importStatus?.status === ImportStatus.COMPLETED ? (
            <>
              <CheckCircleIcon sx={{ color: '#4caf50' }} />
              Importation terminée
            </>
          ) : importStatus?.status === ImportStatus.CANCELLED ? (
            <>
              <CancelIcon sx={{ color: '#ff9800' }} />
              Importation annulée
            </>
          ) : importStatus?.status === ImportStatus.FAILED ? (
            <>
              <CancelIcon sx={{ color: '#f44336' }} />
              Échec de l'importation
            </>
          ) : (
            'Résultats de l\'importation'
          )}
        </DialogTitle>
        <DialogContent>
          {importStatus ? (
            <Box sx={{ py: 2 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              {/* Progress Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {importStatus.status_message || 'Traitement...'}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {importStatus.progress_percentage || 0}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={importStatus.progress_percentage || 0}
                  sx={{
                    height: 12,
                    borderRadius: 6,
                    backgroundColor: colors.grey[200],
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 6,
                      backgroundColor: 
                        importStatus.status === ImportStatus.COMPLETED ? '#4caf50' :
                        importStatus.status === ImportStatus.FAILED ? '#f44336' :
                        importStatus.status === ImportStatus.CANCELLED ? '#ff9800' :
                        colors.primary[500],
                    },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {importStatus.processed_count || 0} / {importStatus.total_rows || 0} contacts traités
                  </Typography>
                  {isImportInProgress && (
                    <Chip 
                      label="En cours" 
                      size="small" 
                      sx={{ 
                        bgcolor: '#cce5ff', 
                        color: '#004085',
                        fontWeight: 500,
                        fontSize: 11,
                      }} 
                    />
                  )}
                </Box>
              </Box>
              
              {/* Statistics */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: 2, 
                mb: 3,
                p: 2,
                backgroundColor: colors.grey[50],
                borderRadius: 2,
              }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#4caf50' }}>
                    {importStatus.success_count || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Importés
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#ff9800' }}>
                    {importStatus.skipped_count || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Ignorés
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#f44336' }}>
                    {importStatus.error_count || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Erreurs
                    </Typography>
                </Box>
                  </Box>
                  
              {/* Duplicates Info */}
              {importStatus.duplicates && importStatus.duplicates.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600} color="warning.main" gutterBottom>
                    ⚠️ Emails en double ignorés :
                      </Typography>
                  <Box sx={{ pl: 2, maxHeight: 100, overflowY: 'auto' }}>
                    {importStatus.duplicates.map((email, idx) => (
                      <Typography key={idx} variant="caption" display="block" color="text.secondary">
                        • {email}
                            </Typography>
                          ))}
                    {importStatus.skipped_count > importStatus.duplicates.length && (
                      <Typography variant="caption" color="text.secondary">
                        ... et {importStatus.skipped_count - importStatus.duplicates.length} autres
                            </Typography>
                          )}
                        </Box>
                    </Box>
                  )}
                  
              {/* Errors Info */}
              {importStatus.error_details && importStatus.error_details.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600} color="error" gutterBottom>
                    ❌ Détails des erreurs :
                  </Typography>
                  <Box sx={{ pl: 2, maxHeight: 100, overflowY: 'auto' }}>
                    {importStatus.error_details.map((err, idx) => (
                      <Typography key={idx} variant="caption" display="block" color="error">
                              Ligne {err.row}: {err.error}
                            </Typography>
                          ))}
                        </Box>
                    </Box>
                  )}
              
              {/* Success message */}
              {isImportComplete && importStatus.success_count > 0 && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {importStatus.success_count} contact(s) importé(s) avec succès !
                </Alert>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress size={60} />
              <Typography variant="body1" color="text.secondary" mt={3}>
                Démarrage de l'importation...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isImportInProgress && (
              <Button
                onClick={handleCancelImport}
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                sx={{ fontWeight: 600 }}
              >
                Annuler
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isImportInProgress && (
              <Button
                onClick={handleContinueInBackground}
                variant="outlined"
                startIcon={<HistoryIcon />}
                sx={{ 
                  fontWeight: 600,
                  borderColor: colors.primary[500],
                  color: colors.primary[500],
                }}
              >
                Continuer en arrière-plan
              </Button>
            )}
          <Button
              onClick={isImportInProgress ? handleContinueInBackground : handleCloseImportDialog}
            variant="contained"
            sx={{
              px: 4,
              py: 1,
              fontWeight: 600,
                backgroundColor: isImportComplete && importStatus?.success_count > 0 ? '#4caf50' : colors.primary[500],
                '&:hover': {
                  backgroundColor: isImportComplete && importStatus?.success_count > 0 ? '#45a049' : colors.primary[600],
                }
            }}
          >
              {isImportComplete && importStatus?.success_count > 0 ? 'Voir les contacts' : 'Fermer'}
          </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Background import notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={15000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity="info" 
          sx={{ 
            width: '100%',
            minWidth: '400px',
            '& .MuiAlert-action': {
              alignItems: 'center',
            }
          }}
          action={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleGoToContacts}
                sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                Voir les contacts
              </Button>
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleSnackbarClose}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          }
        >
          <Box>
            <Typography variant="body2" fontWeight={600}>
              Import en cours en arrière-plan
            </Typography>
            <Typography variant="caption">
              Consultez "Historique des imports" dans la barre de navigation pour suivre la progression.
            </Typography>
          </Box>
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactImport;
